import { useSelector } from 'react-redux';
import { Tooltip } from '@chakra-ui/react';
import { parseDate, isAfter, getFormatDateFromStoreDate } from 'shared-components';
import { IEnrolmentStatus } from '../../../types';
import { TravelStatusEnum } from 'types';
import { ApplicationState } from '../../../store';
import { hasPerm, P } from '../../../utilities/roles/permissions';
import { SidebarItem } from '../../molecules/studentSidebar/SidebarItem';
import { useTranslation } from "react-i18next";

interface IProps {
  arrivalStatus: IEnrolmentStatus['travelStatusSection'];
  onEdit: () => void;
  lastAcceptanceDate: string | undefined;
}

export const ArrivalStatus = ({ arrivalStatus, onEdit, lastAcceptanceDate = '' }: IProps) => {
    const { t } = useTranslation('common');
  const hasAccess = useSelector(({ auth }: ApplicationState) => hasPerm(auth.role, P.LATE_ARRIVAL_VIEW));
  const bookedDate = arrivalStatus.bookedDate ? parseDate(arrivalStatus.bookedDate) : null;
  const acceptanceDate = lastAcceptanceDate ? parseDate(lastAcceptanceDate) : null;
  const isBookedOutdated =
    !!lastAcceptanceDate &&
    !!arrivalStatus.bookedDate &&
    hasAccess &&
    bookedDate &&
    acceptanceDate &&
    isAfter(bookedDate, acceptanceDate);

  const renderReason = (reason: string | undefined) => {
    if (reason?.length && reason.length > 25) {
      return (
        <Tooltip hasArrow placement="bottom" label={reason}>
          <div>{reason?.slice(0, 25)}...</div>
        </Tooltip>
      );
    }
    return <>{reason}</>;
  };

  const getBadgeProps = () => {
    const status = arrivalStatus.status || TravelStatusEnum.None;
    switch (status) {
      case TravelStatusEnum.TravelCancelled: {
        return {
          value: 'Travel Cancelled',
          badgeProps: { colorScheme: 'red' },
          subValue: renderReason(arrivalStatus.travelCanceledReason),
        };
      }
      case TravelStatusEnum.TravelPrevented: {
        return {
          value: 'Travel Prevented',
          badgeProps: { colorScheme: 'red' },
          subValue: renderReason(arrivalStatus.travelPreventedReason),
        };
      }
      case TravelStatusEnum.Defer: {
        return {
          value: 'Intends to Defer',
          badgeProps: { colorScheme: 'red' },
          subValue: renderReason(arrivalStatus.deferReason),
        };
      }
      case TravelStatusEnum.Withdraw: {
        return {
          value: 'Intends to Withdraw',
          badgeProps: { colorScheme: 'red' },
          subValue: renderReason(arrivalStatus.withdrawReason),
        };
      }
      case TravelStatusEnum.Arrived: {
        return {
          value: `Arrived ${getFormatDateFromStoreDate(arrivalStatus.arrivalDate)}`,
          badgeProps: { colorScheme: 'green' },
        };
      }
      case TravelStatusEnum.Booked: {
        return {
          value: `Booked ${getFormatDateFromStoreDate(arrivalStatus.bookedDate)}`,
          badgeProps: { colorScheme: isBookedOutdated ? 'orange' : 'cyan' },
        };
      }
      case TravelStatusEnum.None: {
        return {
          value: 'None',
          isBadge: false,
        };
      }
      case TravelStatusEnum.NoBookingYet: {
        return {
          value: 'No Booking Yet',
          isBadge: false,
        };
      }
      default: {
        return {
          value: status,
          isBadge: false,
        };
      }
    }
  };

  return <SidebarItem label={t('common:arrivalStatus')} onClickEdit={onEdit} isBadge {...getBadgeProps()} />;
};
